import { DataApiResponseLegacy, ExpandSearchGroup, MovieShow, MovieShowMovie } from '@digilize/shared/definitions/src';
import { endOfDay, toISO8601 } from '@digilize/shared/utils/helpers/src';
import { Observable } from 'rxjs';

/**
 * The slice of `MovieApiService` this needs. Narrowed to two calls so the state stays plain
 * TypeScript - no Angular to stand up before it can be tested.
 */
export interface ShowSelectorApi {
  getShowMovies: (params: any) => Observable<DataApiResponseLegacy<MovieShowMovie[]>>;
  getShows: (params: any) => Observable<DataApiResponseLegacy<MovieShow[]>>;
}

/** A movie in the picker, with its showtimes filled in once the operator opens it. */
export type ShowSelectorGroup = ExpandSearchGroup<MovieShow>;

export interface ShowSelectorDateRange {
  from?: string;
  to?: string;
}

/**
 * State behind the dashboard's show filter (PAUL-3562).
 *
 * Lists movies rather than showtimes, and fetches a movie's showtimes only when it is opened. That is
 * what keeps the request count honest: paging a flat list of shows while the rows on screen are
 * collapsed movie headers means the panel never looks full, and anything that pages towards "the list
 * looks full" fetches the whole schedule in a burst.
 *
 * Shared by the orders and reservations lists, which differ only in which form controls hold the date
 * range, so the two filters cannot drift apart.
 */
export class ShowSelectorState {
  /** Movies per page. Large enough that the panel scrolls, which is what drives paging. */
  static readonly MOVIES_PER_FETCH = 20;
  /**
   * Cap on one movie's showtimes. A movie's run inside a date window is far shorter than this; the cap
   * only exists so a pathological one cannot pull an unbounded response.
   */
  static readonly SHOWS_PER_MOVIE = 200;

  groups: ShowSelectorGroup[] = [];
  selected: MovieShow[] = [];
  loading = false;
  recordsFiltered = 0;
  searchValue = '';

  // Identifies the query, not the request: it moves only when the search term or date range changes,
  // so a response that belongs to a window the user has left is dropped. Paging must NOT move it, or
  // scrolling for more movies would throw away showtimes that are still on their way.
  private requestToken = 0;

  constructor(
    private movieApiService: ShowSelectorApi,
    private getShopId: () => string,
    private getDateRange: () => ShowSelectorDateRange,
    private onError: (error: any) => void
  ) {}

  get hasMore(): boolean {
    return this.recordsFiltered > this.groups.length;
  }

  get selectedIds(): string[] {
    return this.selected.map((show) => show.id);
  }

  search(value: string) {
    this.searchValue = value;
    this.reset();
    if (value?.length) {
      this.loadMovies();
    }
  }

  /** The date range moved, so every page already loaded is for the wrong window. */
  refresh() {
    this.reset();
    if (this.searchValue?.length) {
      this.loadMovies();
    }
  }

  reset() {
    this.requestToken++;
    this.groups = [];
    this.recordsFiltered = 0;
    this.loading = false;
  }

  clear() {
    this.reset();
    this.selected = [];
    this.searchValue = '';
  }

  loadMore() {
    if (this.loading || !this.hasMore) return;
    this.loadMovies();
  }

  loadMovies() {
    const token = this.requestToken;
    this.loading = true;

    this.movieApiService.getShowMovies(this.movieQueryParams).subscribe(
      (res: DataApiResponseLegacy<MovieShowMovie[]>) => {
        if (token !== this.requestToken) return;
        this.groups = [...this.groups, ...(res.data ?? []).map((movie) => this.toGroup(movie))];
        // An empty page ends the list whatever the count says, so a count that disagrees with the page
        // size can't keep the panel asking for more forever.
        this.recordsFiltered = res.data?.length ? res.recordsFiltered : this.groups.length;
        this.loading = false;
      },
      (err) => {
        if (token !== this.requestToken) return;
        this.loading = false;
        this.onError(err);
      }
    );
  }

  /** Called the first time a movie is opened. */
  loadGroup(group: ShowSelectorGroup) {
    if (group.loaded || group.loading) return;

    const token = this.requestToken;
    group.loading = true;

    this.movieApiService.getShows(this.showQueryParams(group.key)).subscribe(
      (res: DataApiResponseLegacy<MovieShow[]>) => {
        // Cleared before the token check, so a group left over from a superseded query never keeps a
        // spinner running.
        group.loading = false;
        if (token !== this.requestToken) return;
        group.items = (res.data ?? []).map((show) => ({ ...show, selected: this.isSelected(show) }));
        group.loaded = true;
        this.syncGroupSelection(group);
      },
      (err) => {
        group.loading = false;
        if (token !== this.requestToken) return;
        this.onError(err);
      }
    );
  }

  add(show: MovieShow) {
    if (!this.isSelected(show)) {
      this.selected = [...this.selected, show];
    }
    this.markSelection(show, true);
  }

  remove(show: MovieShow) {
    this.selected = this.selected.filter((selected) => selected.id !== show.id);
    this.markSelection(show, false);
  }

  private isSelected(show: MovieShow): boolean {
    return this.selected.some((selected) => selected.id === show.id);
  }

  private markSelection(show: MovieShow, selected: boolean) {
    const group = this.groups.find((candidate) => candidate.key === show.movie_id);
    if (!group) return;

    group.items = group.items.map((item) => (item.id === show.id ? { ...item, selected } : item));
    this.syncGroupSelection(group);
  }

  private syncGroupSelection(group: ShowSelectorGroup) {
    group.selectedCount = group.items.filter((item) => item.selected).length;
    // A new array so the picker sees the change: it only rebuilds its expansion state when the
    // reference moves, and mutating in place would leave the header badge stale.
    this.groups = [...this.groups];
  }

  private toGroup(movie: MovieShowMovie): ShowSelectorGroup {
    return {
      key: movie.movie_id,
      label: movie.movie_title,
      count: movie.show_count,
      selectedCount: this.selected.filter((show) => show.movie_id === movie.movie_id).length,
      items: [],
      loading: false,
      loaded: false,
    };
  }

  private get movieQueryParams() {
    return {
      shop: this.getShopId(),
      'search.value': this.searchValue,
      length: ShowSelectorState.MOVIES_PER_FETCH,
      start: this.groups.length,
      // Newest play date first, whatever the search term: the show an operator is looking for is almost
      // always the next one or the last one, not something from three years ago.
      order_column_descending: true,
      ...this.dateParams,
    } as any;
  }

  private showQueryParams(movieId: string) {
    return {
      shop: this.getShopId(),
      movie: movieId,
      length: ShowSelectorState.SHOWS_PER_MOVIE,
      start: 0,
      order_column_name: 'start',
      order_column_descending: true,
      ...this.dateParams,
    } as any;
  }

  /**
   * `from`/`to` on both endpoints filter on the show's own start, so the page's date range narrows the
   * picker to shows that play in it. `to` is stretched to the end of its day: the comparison is against
   * the show's start time, and a range ending at midnight would drop every show on the last day picked.
   *
   * With no range the window still has to reach back before today, or searching for a show that has
   * already played finds nothing - both endpoints default to shows that have not finished yet.
   */
  private get dateParams() {
    const { from, to } = this.getDateRange();

    return {
      from: from ? toISO8601(new Date(from), true) : new Date(Date.UTC(1900, 0, 1)).toISOString(),
      to: to ? toISO8601(endOfDay(new Date(to))) : '',
    };
  }
}
